import { Box, Button, Paper, Snackbar, Typography } from '@mui/material';
import { useCallback, useState } from 'react';
import { db } from '../instant';
import { APP_SETTINGS_ID } from '../constants';
import DefaultLayout from '../layouts/DefaultLayout';
import GlobalHeader from '../components/GlobalHeader';
import WelcomeDocInput from '../components/WelcomeDocInput';
import { usePermissions } from '../hooks/usePermissions';
import PermissionDenied from '../components/PermissionDenied';
import type { WelcomeDocRef } from '../utils/welcomeDocUrl';

export default function SettingsScreen() {
    const { isAdmin, isLoading: permissionsLoading } = usePermissions();
    const [draft, setDraft] = useState<WelcomeDocRef | null>(null);
    const [saveSuccess, setSaveSuccess] = useState(false);

    const { data } = db.useQuery({
        appSettings: {
            $: { where: { id: APP_SETTINGS_ID } },
        },
    });

    const settings = data?.appSettings?.[0];
    const currentRef: WelcomeDocRef = draft ?? {
        docId: settings?.welcomeDocId ?? null,
        tabId: settings?.welcomeTabId ?? null,
    };

    const handleSave = useCallback(async () => {
        await db.transact(
            db.tx.appSettings[APP_SETTINGS_ID].update({
                welcomeDocId: currentRef.docId || undefined,
                welcomeTabId: currentRef.tabId || undefined,
            })
        );
        setDraft(null);
        setSaveSuccess(true);
    }, [currentRef.docId, currentRef.tabId]);

    const handleSnackbarClose = useCallback(() => {
        setSaveSuccess(false);
    }, []);

    if (!permissionsLoading && !isAdmin) {
        return <PermissionDenied requiredPermission="Admin (Settings)" />;
    }

    return (
        <DefaultLayout>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flexGrow: 1,
                    width: '100%',
                    maxWidth: 600,
                    mx: 'auto',
                    padding: 2,
                    boxSizing: 'border-box',
                }}
            >
                <GlobalHeader title="Settings" />
                <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
                        Global info document
                    </Typography>
                    <WelcomeDocInput
                        value={currentRef}
                        onChange={setDraft}
                        label="Google Docs URL"
                        helperText="Paste a Google Docs link; only document ID and optional tab are stored."
                    />
                    <Button variant="contained" onClick={handleSave} sx={{ mt: 2 }}>
                        Save
                    </Button>
                </Paper>
            </Box>
            <Snackbar open={saveSuccess} autoHideDuration={3000} onClose={handleSnackbarClose} message="Settings saved." />
        </DefaultLayout>
    );
}
